import { ATTRIBUTION_KEYS, META_FBC_COOKIE, META_FBP_COOKIE, SNAP_CLICK_ID_URL_PARAMS } from 'constants/attribution';
import { getCookie, getLocalStorageWithExpiration } from 'utils/storage';

// The AppsFlyer Web SDK stores the visitor's AppsFlyer id in this cookie.
// Confirm the exact name in DevTools once the SDK is live — the beta SDK may
// differ; this is the only line to change if so.
const AF_USER_ID_COOKIE = 'afUserId';

interface ClickIdHeaderRequest {
    baseURL?: string;
    url?: string;
}

// Keep this list aligned with svc-api's signup-attribution header contract.
// msclkid and dclid remain attribution-touchpoint fields; svc-api does not
// consume them as signup-attribution headers.
const CLICK_ID_HEADERS = [
    { urlParams: ['gclid'], storageKey: 'GCLID', header: 'X-Gclid' },
    { urlParams: ['fbclid'], storageKey: 'FBCLID', header: 'X-Fbclid' },
    { urlParams: ['rdt_cid'], storageKey: 'RDT_CID', header: 'X-Rdt-Cid' },
    { urlParams: ['ttclid'], storageKey: 'TTCLID', header: 'X-Ttclid' },
    { urlParams: ['twclid'], storageKey: 'TWCLID', header: 'X-Twclid' },
    { urlParams: SNAP_CLICK_ID_URL_PARAMS, storageKey: 'SC_CID', header: 'X-Sc-Cid' },
] as const satisfies ReadonlyArray<{
    urlParams: readonly string[];
    storageKey: keyof typeof ATTRIBUTION_KEYS;
    header: string;
}>;

const isV1Route = (resolvedUrl: string): boolean => resolvedUrl.includes('/v1/');

/**
 * Returns attribution click ID headers for inclusion in API requests.
 * Reads from the current URL first, then localStorage using the same keys as the
 * rest of the attribution system. The URL path covers API calls made before the
 * route-attribution effect persists an inbound click ID.
 * Only includes headers for IDs that are present and non-empty.
 * Safe to call in SSR context — getLocalStorageWithExpiration returns null when
 * localStorage is unavailable.
 *
 * Click ID headers are only attached to V1 exchange routes — V2 routes never
 * receive them (the V2 backend doesn't read these headers).
 */
export const getClickIdHeaders = (request: ClickIdHeaderRequest): Record<string, string> => {
    const resolvedUrl = `${request.baseURL ?? ''}${request.url ?? ''}`;
    if (!isV1Route(resolvedUrl)) return {};

    const headers: Record<string, string> = {};
    let urlParams: URLSearchParams | null = null;
    if (typeof window !== 'undefined') {
        try {
            urlParams = new URLSearchParams(window.location.search);
        } catch {
            urlParams = null;
        }
    }

    for (const { urlParams: keys, storageKey, header } of CLICK_ID_HEADERS) {
        const fromUrl = keys.map((key) => urlParams?.get(key)).find(Boolean);
        const value = fromUrl ?? getLocalStorageWithExpiration(ATTRIBUTION_KEYS[storageKey]);
        if (value) headers[header] = value;
    }

    const afUserId = getCookie(AF_USER_ID_COOKIE);
    if (afUserId) headers['X-AppsFlyer-Web-Id'] = afUserId;

    // localStorage mirrors outlive Safari ITP's short caps on the script-set /f cookies.
    const fbp = getCookie(META_FBP_COOKIE) ?? getLocalStorageWithExpiration(ATTRIBUTION_KEYS.FBP);
    if (fbp) headers['X-Fbp'] = fbp;

    const fbc = getCookie(META_FBC_COOKIE) ?? getLocalStorageWithExpiration(ATTRIBUTION_KEYS.FBC);
    if (fbc) headers['X-Fbc'] = fbc;

    return headers;
};
